import React from "react";
import { styled } from "styled-components";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const TermsSection = styled.section`
  padding: 4rem 1rem;
  background: #fff;
  min-height: 80vh;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-family: sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: #e97d7d;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const EffectiveDate = styled.p`
  text-align: center;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 3rem;
  font-style: italic;

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 2rem;
  }
`;

const Content = styled.div`
  color: #2c3e50;
  font-size: 1rem;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;

  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #e97d7d;
  margin-bottom: 1rem;
  margin-top: 2rem;

  &:first-child {
    margin-top: 0;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  text-align: justify;

  @media (max-width: 480px) {
    text-align: left;
  }
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 1.5rem;

  @media (max-width: 480px) {
    padding-left: 1.25rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const HighlightBox = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #e97d7d;
  margin: 1.5rem 0;
  font-style: italic;

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin: 1.25rem 0;
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #e97d7d;
  margin-top: 2rem;

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin-top: 1.5rem;
  }
`;

const ContactTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.75rem;

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const ContactText = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 0.95rem;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const AnimatedSection = styled.div<{ isVisible: boolean; delay: number }>`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? "translateY(0)" : "translateY(30px)"};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(props) => props.delay}ms;
`;

const TermsAndConditions: React.FC = () => {
  const [shouldAnimate, setShouldAnimate] = React.useState(false);
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({
    triggerOnce: false,
  });

  React.useEffect(() => {
    setShouldAnimate(false);
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <TermsSection ref={sectionRef}>
      <Container>
        <AnimatedSection isVisible={shouldAnimate} delay={0}>
          <Title>⚖️ Craft Bin – Terms and Conditions</Title>
          <EffectiveDate>Effective Date: October 27, 2025</EffectiveDate>
        </AnimatedSection>

        <AnimatedSection isVisible={shouldAnimate} delay={200}>
          <Content>
            <Paragraph>
              These Terms govern your access to and use of the Craft Bin website{" "}
              <a href="https://www.craftbin.ca" style={{ color: "#e97d7d" }}>
                [www.craftbin.ca]
              </a>{" "}
              and any related services. By using our Site, you agree to these
              Terms.
            </Paragraph>

            <Section>
              <SectionTitle>1. General Information</SectionTitle>
              <Paragraph>
                Craft Bin ("we", "us", "our") sells handcrafted products,
                including jewelry, resin crafts, home décor, and pet
                accessories.
              </Paragraph>
              <HighlightBox>
                All items are made with care and may have natural variations.
              </HighlightBox>
            </Section>

            <Section>
              <SectionTitle>2. Use of Website</SectionTitle>
              <Paragraph>You agree to:</Paragraph>
              <List>
                <ListItem>Use our Site only for lawful purposes</ListItem>
                <ListItem>
                  Not attempt to hack, scrape, or disrupt services
                </ListItem>
                <ListItem>
                  Provide accurate and complete information during checkout
                </ListItem>
              </List>
              <Paragraph>
                We reserve the right to refuse or cancel orders for any reason,
                including suspected fraud.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>3. Pricing and Payments</SectionTitle>
              <Paragraph>
                All prices are listed in Canadian Dollars (CAD) unless stated
                otherwise.
              </Paragraph>
              <List>
                <ListItem>
                  Taxes and shipping fees are calculated at checkout.
                </ListItem>
                <ListItem>
                  We reserve the right to update prices or promotions without
                  prior notice.
                </ListItem>
                <ListItem>
                  Payments are processed securely through third-party gateways;
                  we do not store full credit card information.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>4. Shipping and Delivery</SectionTitle>
              <List>
                <ListItem>
                  Orders are shipped via Canada Post or other carriers.
                </ListItem>
                <ListItem>
                  Estimated delivery times are provided at checkout but may
                  vary.
                </ListItem>
                <ListItem>
                  Craft Bin is not liable for delays due to customs, weather, or
                  carrier issues.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>5. Returns and Refunds Policy</SectionTitle>

              <Paragraph>
                <strong>Return Window:</strong> We accept returns on eligible
                items within <strong>14 days</strong> of delivery. The return
                period begins on the day you receive your order.
              </Paragraph>

              <Paragraph>
                <strong>Eligible Items for Return:</strong>
              </Paragraph>
              <List>
                <ListItem>
                  Non-customized, non-personalized items in original, unused
                  condition
                </ListItem>
                <ListItem>
                  Items with original packaging and tags intact
                </ListItem>
                <ListItem>
                  Items that are damaged, defective, or significantly different
                  from the product description
                </ListItem>
              </List>

              <Paragraph>
                <strong>Non-Returnable Items:</strong>
              </Paragraph>
              <List>
                <ListItem>
                  Custom or personalized products (e.g., engraved jewelry,
                  custom pet tags with specific names or designs)
                </ListItem>
                <ListItem>
                  Items that have been used, worn, or damaged by the customer
                </ListItem>
                <ListItem>
                  Items returned after the 14-day return window
                </ListItem>
                <ListItem>
                  Items missing original packaging, tags, or accessories
                </ListItem>
              </List>

              <Paragraph>
                <strong>Return Process:</strong>
              </Paragraph>
              <List>
                <ListItem>
                  <strong>Step 1:</strong> Contact us within 14 days of delivery
                  by{" "}
                  <a href="/contact" style={{ color: "#e97d7d" }}>
                    submitting a return request
                  </a>{" "}
                  or emailing us at support@craftbin.ca with your order number
                  and reason for return.
                </ListItem>
                <ListItem>
                  <strong>Step 2:</strong> We will review your request and
                  provide a Return Authorization (RA) number and return shipping
                  address within 2-3 business days.
                </ListItem>
                <ListItem>
                  <strong>Step 3:</strong> Package the item(s) securely in
                  original packaging (if available) and include the RA number.
                  If original packaging is not available, use appropriate
                  packaging to prevent damage during shipping.
                </ListItem>
                <ListItem>
                  <strong>Step 4:</strong> Ship the return using a trackable
                  shipping method. You are responsible for return shipping costs
                  unless the item is damaged, defective, or we sent the wrong
                  item.
                </ListItem>
                <ListItem>
                  <strong>Step 5:</strong> Once we receive and inspect the
                  returned item(s), we will process your refund.
                </ListItem>
              </List>

              <Paragraph>
                <strong>Refund Processing:</strong>
              </Paragraph>
              <List>
                <ListItem>
                  Refunds will be processed to the original payment method used
                  for purchase within 5-10 business days after we receive and
                  inspect the returned item(s).
                </ListItem>
                <ListItem>
                  Shipping charges (original shipping) are non-refundable unless
                  the return is due to our error (wrong item, damaged item, or
                  defective product).
                </ListItem>
                <ListItem>
                  Return shipping costs are the customer's responsibility unless
                  the return is due to our error.
                </ListItem>
                <ListItem>
                  If you paid with a credit or debit card, refunds may take 1-2
                  additional business days to appear in your account, depending
                  on your bank or card issuer.
                </ListItem>
              </List>

              <Paragraph>
                <strong>Damaged or Defective Items:</strong>
              </Paragraph>
              <Paragraph>
                If you receive a damaged or defective item, please{" "}
                <a href="/contact" style={{ color: "#e97d7d" }}>
                  contact us immediately
                </a>{" "}
                (within 48 hours of delivery) with photos of the damage or
                defect. We will arrange a free return shipping label and process
                a full refund or replacement at no additional cost to you.
              </Paragraph>

              <Paragraph>
                <strong>Exchanges:</strong>
              </Paragraph>
              <Paragraph>
                We do not offer direct exchanges. If you wish to exchange an
                item, please follow the return process and place a new order for
                the desired item. We recommend placing your new order after we
                confirm receipt of your return to ensure item availability.
              </Paragraph>

              <HighlightBox>
                <strong>Important:</strong> All returns must be authorized by us
                before shipping. Unauthorized returns may be refused. Please
                contact us before returning any product to ensure a smooth
                return process.
              </HighlightBox>
            </Section>

            <Section>
              <SectionTitle>6. Product Disclaimer</SectionTitle>
              <List>
                <ListItem>
                  Each handcrafted piece may have slight variations in color,
                  texture, or finish — these are not defects.
                </ListItem>
                <ListItem>
                  Products containing resin or small parts should be kept out of
                  reach of children and pets.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>7. Intellectual Property</SectionTitle>
              <Paragraph>
                All content on the Site (images, text, logo, product photos,
                etc.) is owned by Craft Bin and protected under copyright law.
              </Paragraph>
              <Paragraph>
                You may not reproduce or distribute our content without written
                consent.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>8. Limitation of Liability</SectionTitle>
              <Paragraph>
                Craft Bin is not liable for indirect, incidental, or
                consequential damages resulting from:
              </Paragraph>
              <List>
                <ListItem>Product misuse or allergic reactions</ListItem>
                <ListItem>Website downtime or technical issues</ListItem>
                <ListItem>
                  Delays or loss during shipping beyond our control
                </ListItem>
              </List>
              <Paragraph>
                Total liability shall not exceed the purchase price of the
                product.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>9. Governing Law</SectionTitle>
              <Paragraph>
                These Terms are governed by the laws of the Province of Ontario
                and the federal laws of Canada.
              </Paragraph>
              <Paragraph>
                Any disputes shall be resolved in the courts of Ontario, Canada.
              </Paragraph>
            </Section>

            <ContactInfo>
              <ContactTitle>10. Contact Information</ContactTitle>
              <ContactText>
                For questions about these Terms, send a message here:
                <br />
                📧{" "}
                <a href="/contact" style={{ color: "#e97d7d" }}>
                  contact us
                </a>
                <br />
                📍 Kitchener, Ontario, Canada
              </ContactText>
            </ContactInfo>
          </Content>
        </AnimatedSection>
      </Container>
    </TermsSection>
  );
};

export default TermsAndConditions;
